import { useState } from 'react'
import clsx from 'clsx'
import { EmojiUtils } from 'src/utils/emoji'
import { addReaction, deleteReaction } from 'src/client/publication'
import { PostReactionsProps, ReactionProps, ReactionListItem } from './postReactions.types'
import { Reaction, ReactionCounter, ReactionEmoji, Wrapper } from './postReactions.styles'
import { REACTION_TIMEOUT } from './postReactions.constants'

const PostReactions = ({
  postId,
  reactions,
  className,
  onReaction,
  onReactionSuccess,
  onReactionError
}: PostReactionsProps) => {
  const [loading, setLoading] = useState(false)

  const normalizeReaction = (reactionCode: string) => {
    const reaction = reactions?.find((reaction) => reaction.reactionCode === reactionCode)

    return {
      reactionCode,
      reactionEmoji: EmojiUtils.convertEmojiCodeReceive(reactionCode),
      count: reaction?.count || 0,
      reactedByUser: Boolean(reaction?.reactedByUser)
    }
  }

  /**
   * ❤️ Red Heart [0x2764-0xfe0f]
   * 👍️ Thumbs Up [0x1f44d-0xfe0f]
   * 👏️ Clapping Hands [0x1f44f-0xfe0f']
   * 🙏️ Folded Hands [0x1f64f-0xfe0f]
   * 🎉️ Party Popper [0x1f389-0xfe0f]
   */
  const [reactionList, setReactionList] = useState<ReactionListItem[]>([
    normalizeReaction('0x2764-0xfe0f'),
    normalizeReaction('0x1f44d-0xfe0f'),
    normalizeReaction('0x1f44f-0xfe0f'),
    normalizeReaction('0x1f64f-0xfe0f'),
    normalizeReaction('0x1f389-0xfe0f')
  ])

  const handleDecrementCount = async ({ reactionCode, count }: ReactionProps) => {
    try {
      const newCount = count - 1

      setReactionList((oldReactionList) =>
        oldReactionList.map((reaction) =>
          reaction.reactionCode === reactionCode
            ? { ...reaction, count: newCount, reactedByUser: false }
            : reaction
        )
      )

      onReaction?.({ reactionCode, count: newCount })
      await deleteReaction(postId, reactionCode)
      onReactionSuccess?.()
    } catch (error) {
      const err = error as { statusCode: number; message: string }

      onReactionError?.({
        statusCode: err.statusCode,
        errorMessage: err.message
      })
    }
  }

  const handleIncrementCount = async ({ reactionCode, count }: ReactionProps) => {
    try {
      const newCount = count + 1

      setReactionList((oldReactionList) =>
        oldReactionList.map((reaction) =>
          reaction.reactionCode === reactionCode
            ? { ...reaction, count: newCount, reactedByUser: true }
            : reaction
        )
      )

      onReaction?.({ reactionCode, count: newCount })
      await addReaction(postId, reactionCode)
      onReactionSuccess?.()
    } catch (error) {
      const err = error as { statusCode: number; message: string }

      onReactionError?.({
        statusCode: err.statusCode,
        errorMessage: err.message
      })
    }
  }

  const handleClick = async (reaction: ReactionProps) => {
    setLoading(true)

    if (reaction.reactedByUser) {
      await handleDecrementCount(reaction)
    } else {
      await handleIncrementCount(reaction)
    }

    setTimeout(() => {
      setLoading(false)
    }, REACTION_TIMEOUT)
  }

  return (
    <Wrapper className={clsx(className, '_d-flex _align-items-center _flex-wrap')}>
      {reactionList.map((reaction) => (
        <Reaction
          key={reaction.reactionCode}
          className="_border-gray-200 _border _rounded-pill _text-1 _d-flex _justify-content-center _align-items-center _overflow-hidden _px-2"
          disabled={loading}
          onClick={() => handleClick(reaction)}
        >
          <ReactionEmoji
            className="_h-full _d-flex _justify-content-center _align-items-center _position-relative _text-1"
            $reactionEmoji={reaction.reactionEmoji}
            $decrement={!reaction.reactedByUser}
          >
            {reaction.reactionEmoji}
          </ReactionEmoji>

          <ReactionCounter className="_ml-2">{reaction.count}</ReactionCounter>
        </Reaction>
      ))}
    </Wrapper>
  )
}

export default PostReactions
